import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import Breadcrumbs from '../components/Breadcrumbs';
import '../App.css';
import '../components/SearchResults.css';
import './DetailPage.css';

interface CodeArticle {
    id: string;
    numero: string;
    text: string;
    code_name?: string;
    law_title?: string;
    modif_law_title?: string;
    doc_type?: string;
    code_pdf_url?: string;
    code_pdf_ar_url?: string;
}

const CodeArticleDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [article, setArticle] = useState<CodeArticle | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchArticle();
    }, [id, i18n.language]);

    const fetchArticle = async () => {
        try {
            setLoading(true);
            // Appel API pour récupérer l'article par ID
            const response = await api.get(`/code-article/${id}?lang=${i18n.language}`);
            setArticle(response.data);
        } catch (err) {
            console.error('Error fetching article:', err);
            setError('Erreur lors du chargement de l\'article');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="container mt-5">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">{error || 'Article non trouvé'}</div>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left me-2"></i>Retour
                </button>
            </div>
        );
    }

    const displayContent = article.text;

    const breadcrumbs = [
        {
            label: t('search.types.code_article'),
            onClick: () => navigate(-1)
        },
        { label: `${t('search.article')} ${article.numero}` }
    ];

    return (
        <div className="detail-page">
            <Breadcrumbs items={breadcrumbs} />

            {/* Card 1: Attributes / Metadata */}
            <div className="result-card type-code mb-4 animate-fade-in">
                <div className="result-card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <div className="d-flex gap-2 mb-2">
                                <span className="premium-badge badge-code px-3 py-2 rounded-pill">
                                    <i className="bi bi-book me-2"></i>{t('search.types.code_article')}
                                </span>
                                {article.doc_type && (
                                    <span className="badge bg-secondary px-3 py-2 rounded-pill">
                                        {article.doc_type}
                                    </span>
                                )}
                            </div>
                            <h1 className="h3 fw-bold text-dark mb-2 result-title" style={{ fontSize: '1.75rem' }}>
                                {t('search.article')} {article.numero}
                            </h1>
                        </div>
                        <button
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => navigate(-1)}
                        >
                            <i className="bi bi-arrow-left me-2"></i>{t('detail.back')}
                        </button>
                    </div>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="row g-4">
                        {article.code_name && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-primary me-3">
                                        <i className="bi bi-journal-code fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('search.code')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{article.code_name}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {article.law_title && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-primary me-3">
                                        <i className="bi bi-file-earmark-text fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.law')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{article.law_title}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {article.modif_law_title && (
                            <div className="col-12">
                                <div className="p-3 bg-info bg-opacity-10 rounded-3 border border-info border-opacity-25">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-pencil-square text-info fs-5 me-3"></i>
                                        <div>
                                            <h6 className="text-info mb-1 text-uppercase small fw-bold">{t('detail.modifiedBy')}</h6>
                                            <p className="mb-0 fw-medium text-dark">{article.modif_law_title}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Card 2: Content */}
            <div className="result-card type-code mb-4 animate-fade-in">
                <div className="result-card-body p-4">
                    <h4 className="fw-bold mb-4 text-dark border-start border-4 border-primary ps-3">
                        {t('detail.fullText')}
                    </h4>
                    <div
                        className="content-text"
                        style={{ fontSize: '1.1rem', lineHeight: '1.8', fontFamily: 'Georgia, serif' }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayContent) }}
                    />
                </div>
            </div>


        </div>
    );
};

export default CodeArticleDetail;
